import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/Layout";
import { api, errMsg, fmtDate, fullName } from "@/lib/api";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [mine, setMine] = useState(true);
  const [done, setDone] = useState(false);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [digest, setDigest] = useState(null);
  const load = () => api.get("/tasks", { params: { mine, done } }).then((r) => setTasks(r.data));
  useEffect(() => { load(); }, [mine, done]); // eslint-disable-line
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/tasks", { title, due_at: due ? new Date(due).toISOString() : undefined }); setTitle(""); setDue(""); load(); } catch (x) { toast.error(errMsg(x)); }
  };
  const toggle = async (t) => { await api.patch(`/tasks/${t.id}`, { done: !t.done }); load(); };
  const del = async (t) => { await api.delete(`/tasks/${t.id}`); load(); };
  const overdue = (t) => !t.done && new Date(t.due_at) < new Date();

  return (
    <div>
      <PageHeader eyebrow="Reminders" title="Tasks">
        <Button variant="outline" onClick={() => api.get("/digest/preview").then((r) => setDigest(r.data.html))} data-testid="digest-preview-btn"><Mail size={15} className="mr-1" />Preview daily digest</Button>
      </PageHeader>
      <div className="p-6 lg:p-10 space-y-5 max-w-4xl">
        <form onSubmit={add} className="flex flex-wrap gap-2 bg-white border rounded-xl p-3">
          <Input className="flex-1 min-w-[220px]" placeholder="New task… e.g. Call Marcus about proposal" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="task-title-input" />
          <Input type="datetime-local" className="w-56" value={due} onChange={(e) => setDue(e.target.value)} data-testid="task-due-input" />
          <Button data-testid="task-add-btn"><Plus size={15} className="mr-1" />Add</Button>
        </form>
        <div className="flex gap-6 text-sm text-slate-600">
          <label className="flex items-center gap-2"><Switch checked={mine} onCheckedChange={setMine} data-testid="tasks-mine-toggle" />Assigned to me</label>
          <label className="flex items-center gap-2"><Switch checked={done} onCheckedChange={setDone} data-testid="tasks-done-toggle" />Show completed</label>
        </div>
        <div className="bg-white border rounded-xl divide-y" data-testid="task-list">
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-3.5" data-testid={`task-${t.id}`}>
              <Checkbox checked={t.done} onCheckedChange={() => toggle(t)} data-testid={`task-toggle-${t.id}`} />
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${t.done ? "line-through text-slate-400" : ""}`}>{t.title}</div>
                <div className="text-xs text-slate-500 flex gap-2">
                  <span className={overdue(t) ? "text-red-600 font-semibold" : ""}>{overdue(t) ? "Overdue · " : "Due "}{fmtDate(t.due_at)}</span>
                  {t.lead && <Link to={`/leads/${t.lead.id}`} className="text-blue-600 hover:underline">{t.lead.company_name} ({fullName(t.lead)})</Link>}
                  {t.auto && <span className="text-violet-600">auto</span>}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => del(t)} data-testid={`task-delete-${t.id}`}><Trash2 size={14} /></Button>
            </div>))}
          {!tasks.length && <div className="p-10 text-center text-sm text-slate-500">Nothing here.</div>}
        </div>
      </div>
      <Dialog open={!!digest} onOpenChange={(o) => !o && setDigest(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="digest-dialog">
          <DialogHeader><DialogTitle>Daily digest preview</DialogTitle></DialogHeader>
          <div className="text-sm prose-email [&_h2]:font-bold [&_h2]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_ul]:list-disc [&_ul]:pl-5" dangerouslySetInnerHTML={{ __html: digest || "" }} />
          <p className="text-xs text-slate-500">Sent automatically at 8:00 (UK time) on weekdays to users with digest enabled.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
